/**
 * Development and staging only: made-up data for the dashboard preview at
 * /dev/crm-preview, so its pages show what a working org sees (stats,
 * reports, users) instead of signed-out empty and error states. Every
 * backend call goes through firebase.ts's callable wrapper, which asks
 * previewResponse() first; it answers only on the preview page, never in
 * production, and only for the calls named below. Nothing here is real.
 */
import { isProduction } from "./config";

export const DEV_PREVIEW_PATH = "/dev/crm-preview";

export const isDevPreviewActive = (): boolean => {
  if (isProduction || typeof window === "undefined") return false;
  return window.location.pathname.startsWith(DEV_PREVIEW_PATH);
};

/* A fixed pseudo-random sequence, so two runs of the preview draw the same charts. */
const seeded = (seed: number) => {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

const dayKeys = (start: Date, end: Date): string[] => {
  const out: string[] = [];
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (d <= last && out.length < 400) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
    d.setDate(d.getDate() + 1);
  }
  return out;
};

/* Seeded by the window's first day, so each period (and the one before it) draws its own numbers. */
const daySeed = (iso: string) => Math.floor(new Date(iso).getTime() / 86_400_000) % 100_000 || 1;

const chartRangeStats = (req: { startDate: string; endDate: string }) => {
  const rand = seeded(daySeed(req.startDate));
  const result: Record<string, Record<string, number>> = {};
  const totals = { prospectsChecked: 0, valid: 0, invalid: 0, questionable: 0, enrichedByBoring: 0, bouncesPrevented: 0 };
  for (const key of dayKeys(new Date(req.startDate), new Date(req.endDate))) {
    const weekday = new Date(`${key}T12:00:00`).getDay();
    const base = weekday === 0 || weekday === 6 ? 40 : 160;
    const checked = Math.round(base + rand() * base * 0.6);
    const invalid = Math.round(checked * (0.08 + rand() * 0.05));
    const questionable = Math.round(checked * (0.1 + rand() * 0.05));
    const valid = checked - invalid - questionable;
    const enriched = Math.round(checked * (0.12 + rand() * 0.06));
    const bounces = Math.round(invalid * 0.9);
    result[new Date(`${key}T12:00:00`).toISOString()] = { prospectsChecked: checked, valid, invalid, questionable, enrichedByBoring: enriched, bouncesPrevented: bounces };
    totals.prospectsChecked += checked;
    totals.valid += valid;
    totals.invalid += invalid;
    totals.questionable += questionable;
    totals.enrichedByBoring += enriched;
    totals.bouncesPrevented += bounces;
  }
  return { result: { ...result, totals }, status: 200 };
};

const PEOPLE = [
  ["Dana", "Whitfield", "dana.whitfield@acmepreview.com", "Super Admin", ["Sales"]],
  ["Marcus", "Lee", "marcus.lee@acmepreview.com", "Sales Rep", ["Sales", "EMEA"]],
  ["Priya", "Natarajan", "priya.natarajan@acmepreview.com", "Sales Rep", ["Sales"]],
  ["Tom", "Becker", "tom.becker@acmepreview.com", "SDR", ["SDR"]],
  ["Elena", "Rossi", "elena.rossi@acmepreview.com", "SDR", ["SDR", "EMEA"]],
  ["Jordan", "Price", "jordan.price@acmepreview.com", "Sales Manager", ["Sales"]],
  ["Aisha", "Okafor", "aisha.okafor@acmepreview.com", "SDR", ["SDR"]],
  ["Ben", "Carter", "ben.carter@acmepreview.com", "Marketing", ["Marketing"]],
  ["Sofia", "Lindqvist", "sofia.lindqvist@acmepreview.com", "Sales Rep", ["EMEA"]],
  ["Noah", "Kim", "noah.kim@acmepreview.com", "Customer Success", ["Success"]],
  ["Grace", "Hall", "grace.hall@acmepreview.com", "Sales Rep", ["Sales"]],
  ["Leo", "Martins", "leo.martins@acmepreview.com", "SDR", ["SDR"]],
] as const;

/** The emails the preview org licenses, so the users table has both kinds. */
export const DEV_PREVIEW_LICENSED_EMAILS = PEOPLE.filter((_, i) => i % 3 !== 2).map((p) => p[2]);

const hubSpotUsers = () => ({
  result: {
    users: PEOPLE.map(([firstName, lastName, email, role, teams], i) => ({
      id: String(1000 + i),
      email,
      firstName,
      lastName,
      archived: i === 9,
      superAdmin: role === "Super Admin",
      roleName: role === "Super Admin" ? undefined : role,
      teams: teams.map((name, j) => ({ id: `${name}-${j}`, name, primary: j === 0 })),
      createdAt: new Date(2026, 0, 5 + i * 9).toISOString(),
    })),
  },
  status: 200,
});

const outreachUsers = () => {
  const roles = Array.from(new Set(PEOPLE.map((p) => p[3])));
  const teams = Array.from(new Set(PEOPLE.flatMap((p) => [...p[4]])));
  return {
    result: {
      users: PEOPLE.map(([firstName, lastName, email, role, userTeams], i) => ({
        id: 2000 + i,
        type: "user",
        attributes: { email, firstName, lastName, locked: i === 9 },
        relationships: {
          profile: { data: { id: String(roles.indexOf(role) + 1), type: "profile" } },
          teams: { data: userTeams.map((t) => ({ id: String(teams.indexOf(t) + 1), type: "team" })) },
        },
      })),
      included: [
        ...roles.map((name, i) => ({ id: String(i + 1), type: "profile", attributes: { name } })),
        ...teams.map((name, i) => ({ id: String(i + 1), type: "team", attributes: { name } })),
      ],
      relationshipsIncluded: true,
    },
    status: 200,
  };
};

const COUNTER_KEYS = [
  "phoneLookups",
  "phoneLookupsFound",
  "phoneVerifications",
  "phoneManualLookups",
  "phoneCostUsdMicros",
  "linkedinConnectsSent",
  "linkedinConnectsAccepted",
  "linkedinMessagesSent",
  "linkedinProfileViews",
  "linkedinReplies",
  "profileRefreshes",
  "profileUpdates",
  "profileFieldsUpdated",
  "jobChangesDetected",
  "exportRequests",
  "exportEmailsFound",
  "extensionEnrichRequests",
] as const;

const insightsReport = (req: { startDate: string; endDate: string }) => {
  const rand = seeded(daySeed(req.startDate) + 7);
  const zero = () => Object.fromEntries(COUNTER_KEYS.map((k) => [k, 0])) as Record<(typeof COUNTER_KEYS)[number], number>;
  const totals = zero();
  const days = dayKeys(new Date(req.startDate), new Date(req.endDate)).map((date) => {
    const day = zero();
    const r = () => rand();
    day.phoneLookups = Math.round(20 + r() * 30);
    day.phoneLookupsFound = Math.round(day.phoneLookups * (0.55 + r() * 0.2));
    day.phoneManualLookups = Math.round(r() * 8);
    day.phoneVerifications = Math.round(30 + r() * 40);
    day.phoneCostUsdMicros = day.phoneLookupsFound * 120_000;
    day.linkedinConnectsSent = Math.round(25 + r() * 20);
    day.linkedinConnectsAccepted = Math.round(day.linkedinConnectsSent * (0.25 + r() * 0.15));
    day.linkedinMessagesSent = Math.round(15 + r() * 15);
    day.linkedinProfileViews = Math.round(40 + r() * 40);
    day.linkedinReplies = Math.round(day.linkedinMessagesSent * (0.1 + r() * 0.1));
    day.profileRefreshes = Math.round(80 + r() * 60);
    day.profileUpdates = Math.round(day.profileRefreshes * (0.15 + r() * 0.1));
    day.profileFieldsUpdated = Math.round(day.profileUpdates * (1.5 + r()));
    day.jobChangesDetected = Math.round(r() * 6);
    day.exportRequests = Math.round(10 + r() * 15);
    day.exportEmailsFound = Math.round(day.exportRequests * (0.6 + r() * 0.2));
    day.extensionEnrichRequests = Math.round(5 + r() * 10);
    for (const k of COUNTER_KEYS) totals[k] += day[k];
    return { date, ...day };
  });
  const byUser = PEOPLE.slice(0, 7).map(([first, last], i) => {
    const share = [0.24, 0.19, 0.16, 0.13, 0.11, 0.1, 0.07][i];
    const row = zero();
    for (const k of COUNTER_KEYS) row[k] = Math.round(totals[k] * share);
    return { user: `${first} ${last}`, ...row };
  });
  return {
    result: {
      days,
      totals,
      byUser,
      linkedin: { replies: totals.linkedinReplies, replyRate: null, acceptanceRate: null, queue: { pending: 46, completed: 1284, failed: 7 } },
    },
    status: 200,
  };
};

const addonStatus = () => {
  const licensed = DEV_PREVIEW_LICENSED_EMAILS;
  const addon = (key: string, name: string, description: string, seats: number, assigned: readonly string[]) => ({
    key,
    name,
    description,
    configured: true,
    enabled: true,
    source: "subscription",
    billing: { status: "active", subscriptionItemId: `si_${key}`, priceId: `price_${key}`, seats, interval: "monthly", unitAmount: 2000 },
    seatLimit: seats,
    assigned: [...assigned],
    prices: { monthly: { id: `price_${key}`, unitAmount: 2000, currency: "usd" }, yearly: null },
  });
  return {
    result: {
      addons: [
        addon("dialer", "Dialer", "Power-dial HubSpot task queues.", 5, licensed.slice(0, 4)),
        addon("phoneEnrichment", "Phone enrichment", "Find direct dials and mobiles.", 5, licensed.slice(0, 3)),
        addon("phoneVerification", "Phone verification", "Check numbers before you call.", 8, licensed.slice(0, 6)),
      ],
      canSelfServe: true,
      licensedEmails: [...licensed],
    },
    status: 200,
  };
};

const DAY = 86_400_000;
const NOW = Date.UTC(2026, 9, 3, 15, 0, 0);

const dialerSettings = () => ({
  enabled: true,
  dispositions: {
    NO_ANSWER: "No Answer",
    BAD_NUMBER: "Wrong Number",
    NO_NUMBER: "No Number",
    LEFT_VOICEMAIL: "Left Voicemail",
    DUPLICATE_TASK: "Duplicate",
    GATEKEEPER: "Gatekeeper",
  },
  flags: {
    AUTOMATED_VOICEMAIL_FLOW: true,
    AUTOMATED_VOICEMAIL_BEHAVIOR: "DROP",
    AUTOMATED_GATEKEEPER_FLOW: false,
    NO_NUMBER_ENRICHMENT: true,
    EARLY_CALL_END_REDIAL: true,
    LOCK_HANGUP_WHILE_RINGING: false,
    RING_TIMEOUT_ENABLED: true,
    RING_TIMEOUT_SECONDS: 25,
  },
  callDelay: { enabled: true, delayMs: 3000, adminLocked: false },
  callingHours: { enabled: true, blockMode: false, minHour: 8, minMinute: 0, maxHour: 18, maxMinute: 0 },
  availableDispositions: ["No Answer", "Wrong Number", "No Number", "Left Voicemail", "Duplicate", "Gatekeeper", "Connected", "Busy"],
});

const linkedInAutomation = () => {
  const actions = ["connect", "message", "view_profile", "connect", "message", "connect"] as const;
  const statuses = ["pending", "pending", "completed", "completed", "failed", "completed"] as const;
  return {
    result: {
      settings: {
        enabled: true,
        actionsPerHour: 12,
        activeHoursStart: 9,
        activeHoursEnd: 17,
        timezone: "America/New_York",
        completeTasks: true,
        whenNotConnected: "wait",
        rescheduleDays: 2,
        stepSelection: "tagged",
      },
      eligible: true,
      needsScopes: false,
      queue: actions.map((actionType, i) => ({
        id: `act-${i}`,
        ownerUid: `u${i % 3}`,
        ownerEmail: PEOPLE[i % 3][2],
        actionType,
        payload: { linkedinUrl: `https://www.linkedin.com/in/preview-${i}`, text: actionType === "message" ? "Thanks for connecting!" : null },
        status: statuses[i],
        scheduledAfter: NOW + (i - 2) * 3_600_000,
        error: statuses[i] === "failed" ? "Profile no longer available" : null,
        outreach: { taskId: `t-${i}`, prospectId: `p-${i}`, prospectName: `${PEOPLE[(i + 5) % PEOPLE.length][0]} ${PEOPLE[(i + 5) % PEOPLE.length][1]}`, stepType: "linkedin", taskCompleted: statuses[i] === "completed" },
        createdAt: NOW - (i + 1) * 5_400_000,
        completedAt: statuses[i] === "completed" ? NOW - i * 3_600_000 : null,
      })),
      analytics: {
        connectsSent: 214,
        connectsAccepted: 71,
        acceptanceRate: 0.33,
        messagesSent: 132,
        replies: 19,
        replyRate: 0.14,
        queue: { pending: 46, completed: 1284, failed: 7 },
        byUser: Object.fromEntries(
          PEOPLE.slice(0, 4).map(([, , email], i) => [email, { connectsSent: 80 - i * 15, connectsAccepted: 26 - i * 5, messagesSent: 50 - i * 9, replies: 7 - i }])
        ),
      },
    },
    status: 200,
  };
};

const exportHistory = (req?: { exportedBy?: string | null }) => {
  const companies = ["Northwind Labs", "Globex", "Initech", "Umbrella Health", "Stark Freight", "Wayne Analytics", "Hooli", "Vandelay Imports"];
  const titles = ["VP Sales", "Head of RevOps", "Sales Director", "CRO", "SDR Manager", "Account Executive", "Marketing Lead", "COO"];
  const rows = companies.map((companyName, i) => {
    const first = PEOPLE[(i + 3) % PEOPLE.length][0];
    const last = ["Moreno", "Fischer", "Nakamura", "Osei", "Duarte", "Kowalski", "Brennan", "Haddad"][i];
    const by = PEOPLE[i % 4];
    return {
      id: `exp-${i}`,
      entityType: "person",
      name: `${first} ${last}`,
      firstName: first,
      lastName: last,
      title: titles[i],
      headline: `${titles[i]} at ${companyName}`,
      about: null,
      companyName,
      jobStartDate: `202${i % 5}-0${(i % 9) + 1}`,
      location: ["New York, NY", "Austin, TX", "London, UK", "Berlin, DE"][i % 4],
      city: null,
      state: null,
      country: null,
      timezone: null,
      pictureUrl: null,
      industry: "Software",
      website: null,
      employees: null,
      description: null,
      linkedinUrl: `https://www.linkedin.com/in/${first.toLowerCase()}-${last.toLowerCase()}`,
      linkedinId: null,
      linkedinUrn: null,
      salesNavUrn: null,
      email: `${first.toLowerCase()}.${last.toLowerCase()}@${companyName.toLowerCase().replace(/[^a-z]/g, "")}.com`,
      emailStatus: i % 4 === 3 ? "questionable" : "valid",
      phone: i % 3 === 0 ? "+1 415 555 01" + String(10 + i) : null,
      destination: "hubspot",
      action: i % 5 === 4 ? "updated" : "created",
      error: null,
      recordId: String(5000 + i),
      recordUrl: null,
      exportedBy: `u${i % 4}`,
      exportedByEmail: by[2],
      exportedByName: `${by[0]} ${by[1]}`,
      exportedAt: NOW - i * DAY * 0.7,
    };
  });
  // ?role=user previews a rep, who only ever sees their own exports.
  const isAdmin = new URLSearchParams(window.location.search).get("role") !== "user";
  const exporters = [0, 1, 2, 3].map((i) => ({ uid: `u${i}`, name: `${PEOPLE[i][0]} ${PEOPLE[i][1]}`, email: PEOPLE[i][2] as string }));
  const wanted = String(req?.exportedBy || "").trim();
  const exportedBy = !isAdmin || wanted === "me" ? "u0" : !wanted || wanted === "all" ? null : exporters.find((e) => e.uid === wanted || e.email === wanted)?.uid || `unknown:${wanted}`;
  return {
    result: {
      rows: exportedBy ? rows.filter((r) => r.exportedBy === exportedBy) : rows,
      isAdmin,
      truncated: false,
      me: "u0",
      exportedBy,
      exporters: isAdmin ? exporters : exporters.slice(0, 1),
      allMyExportsUrl: "https://app.hubspot.com/contacts/0/objectLists/0/filters",
    },
    status: 200,
  };
};

const billingStatus = () => ({
  result: {
    billingType: "stripe",
    plan: "enrich",
    isManuallyBilled: false,
    isPayingCustomer: true,
    isTrial: false,
    isTrialExpired: false,
    trialEligible: false,
    trialDetails: null,
    billing: { subscriptionId: "sub_preview", status: "active", interval: "monthly", seats: 10, currentPeriodEnd: new Date(NOW + 18 * DAY).toISOString(), cancelAtPeriodEnd: false, priceId: "price_enrich_monthly" },
    seatsAssigned: DEV_PREVIEW_LICENSED_EMAILS.length,
    availableLicenses: 10,
    freeSeats: 0,
    trial: { durationDays: 14, maxUsers: 5, verificationsPerUser: 1000 },
    config: {
      publishableKey: "pk_test_preview",
      prices: { monthly: { id: "price_enrich_monthly", unitAmount: 4900, currency: "usd" }, yearly: { id: "price_enrich_yearly", unitAmount: 47000, currency: "usd" } },
      // A legacy Enrich subscriber: Change plan lists the legacy plans and the packages for sale.
      planPrices: Object.fromEntries(
        (
          [
            ["verify", 3900, 39900],
            ["enrich", 5900, 59900],
            ["prospector", 8800, 79900],
            ["data", 7900, 79900],
            ["linkedin", 5900, 59900],
            ["everything", 9900, 99900],
            ["dialFaster", 3900, 39900],
          ] as const
        ).map(([key, monthly, yearly]) => [
          key,
          { monthly: { id: `price_${key}_monthly`, unitAmount: monthly, currency: "usd" }, yearly: { id: `price_${key}_yearly`, unitAmount: yearly, currency: "usd" } },
        ])
      ),
    },
    sellablePlans: ["data", "linkedin", "everything", "dialFaster"],
  },
  status: 200,
});

const billingInvoices = () => ({
  result: {
    invoices: [0, 1, 2, 3].map((i) => ({
      id: `in_${i}`,
      number: `BRNG-00${42 - i}`,
      status: "paid",
      total: 49000,
      currency: "usd",
      created: Math.floor((NOW - (i + 1) * 30 * DAY) / 1000),
      hostedInvoiceUrl: null,
      invoicePdf: null,
    })),
  },
  status: 200,
});

const usageReport = () => {
  const months = [0, 1, 2].map((i) => {
    const d = new Date(Date.UTC(2026, 9 - i, 1));
    const lookups = 640 - i * 90;
    return {
      period: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`,
      phoneLookups: lookups,
      phoneLookupsFound: Math.round(lookups * 0.64),
      phoneVerifications: 1210 - i * 140,
      phoneCostUsd: Math.round(lookups * 0.64 * 0.12 * 100) / 100,
      phoneBillableUsd: Math.round(lookups * 0.64 * 0.125 * 100) / 100,
      extensionEnrichRequests: 310 - i * 40,
      exportRequests: 420 - i * 55,
      exportEmailsFound: 298 - i * 41,
      byUser: {},
    };
  });
  const sum = (k: keyof (typeof months)[number]) => months.reduce((a, m) => a + (m[k] as number), 0);
  return {
    result: {
      months,
      totals: {
        phoneLookups: sum("phoneLookups"),
        phoneLookupsFound: sum("phoneLookupsFound"),
        phoneVerifications: sum("phoneVerifications"),
        phoneCostUsd: sum("phoneCostUsd"),
        phoneBillableUsd: sum("phoneBillableUsd"),
        extensionEnrichRequests: sum("extensionEnrichRequests"),
        exportRequests: sum("exportRequests"),
        exportEmailsFound: sum("exportEmailsFound"),
      },
      matchRate: 0.64,
    },
    status: 200,
  };
};

/* ── the Job changes page: a dozen moves, kept here so approving and rejecting stick for the session ── */

type PreviewJobChange = {
  key: string;
  recordId: string;
  name: string;
  previousTitle: string;
  previousCompany: string;
  newTitle: string;
  newCompany: string;
  owners: string[];
  daysAgo: number;
  status: "pending" | "applied" | "rejected" | "superseded";
  auto?: boolean;
  lastError?: string;
};

const PREVIEW_JOB_CHANGES: PreviewJobChange[] = [
  { key: "a1b2c3d4e5f60001", recordId: "51001", name: "Maya Levi", previousTitle: "Head of Sales", previousCompany: "Wix", newTitle: "VP Sales", newCompany: "monday.com", owners: ["preview@example.com"], daysAgo: 0, status: "pending" },
  { key: "a1b2c3d4e5f60002", recordId: "51002", name: "Daniel Cohen", previousTitle: "Account Executive", previousCompany: "Gong", newTitle: "Senior AE", newCompany: "Snowflake", owners: ["preview@example.com", "dana@example.com"], daysAgo: 1, status: "pending" },
  { key: "a1b2c3d4e5f60003", recordId: "51003", name: "Priya Raman", previousTitle: "RevOps Manager", previousCompany: "Atlassian", newTitle: "Director of RevOps", newCompany: "Lattice", owners: ["dana@example.com"], daysAgo: 2, status: "pending", lastError: "Lattice is not in HubSpot yet, and this organization only adds people at companies that exist." },
  { key: "a1b2c3d4e5f60004", recordId: "51004", name: "Tom Becker", previousTitle: "CTO", previousCompany: "Personio", newTitle: "Founder", newCompany: "Stealth startup", owners: ["preview@example.com"], daysAgo: 3, status: "pending" },
  { key: "a1b2c3d4e5f60005", recordId: "51005", name: "Sara Johansson", previousTitle: "Marketing Lead", previousCompany: "Klarna", newTitle: "CMO", newCompany: "Northvolt", owners: ["alex@example.com"], daysAgo: 4, status: "applied", auto: true },
  { key: "a1b2c3d4e5f60006", recordId: "51006", name: "Luis Ortega", previousTitle: "SDR", previousCompany: "Outreach", newTitle: "Account Executive", newCompany: "Salesloft", owners: ["preview@example.com"], daysAgo: 6, status: "applied" },
  { key: "a1b2c3d4e5f60007", recordId: "51007", name: "Hannah Weiss", previousTitle: "Product Manager", previousCompany: "SAP", newTitle: "Group PM", newCompany: "Celonis", owners: ["dana@example.com"], daysAgo: 8, status: "rejected" },
  { key: "a1b2c3d4e5f60008", recordId: "51008", name: "Noah Friedman", previousTitle: "Sales Director", previousCompany: "Check Point", newTitle: "CRO", newCompany: "Cato Networks", owners: ["alex@example.com"], daysAgo: 9, status: "pending" },
  { key: "a1b2c3d4e5f60009", recordId: "51009", name: "Emma Dubois", previousTitle: "Engineer", previousCompany: "Datadog", newTitle: "Staff Engineer", newCompany: "Mistral AI", owners: ["preview@example.com"], daysAgo: 12, status: "superseded" },
];

const previewJobChangeRow = (j: PreviewJobChange, hubspot: boolean) => {
  const at = Date.now() - j.daysAgo * 86_400_000 - 3_600_000;
  const recordUrl = hubspot ? `https://app.hubspot.com/contacts/42/record/0-1/${j.recordId}` : `https://web.outreach.io/prospects/${j.recordId}/overview`;
  return {
    key: j.key,
    recordId: j.recordId,
    crm: hubspot ? "hubspot" : "outreach",
    name: j.name,
    previousCompany: j.previousCompany,
    previousTitle: j.previousTitle,
    newCompany: j.newCompany,
    newTitle: j.newTitle,
    linkedInUrl: `https://www.linkedin.com/in/${j.name.toLowerCase().replace(/\s+/g, "-")}`,
    recordUrl,
    ownerId: null,
    ownerEmail: j.owners[0] || null,
    ownerEmails: j.owners,
    detectedAt: at,
    status: j.status,
    appliedAt: j.status === "applied" ? at + 60_000 : null,
    auto: !!j.auto,
    outcome: j.status === "applied" ? "updated" : null,
    rejectedAt: j.status === "rejected" ? at + 60_000 : null,
    lastError: j.status === "pending" ? j.lastError || null : null,
    updatedAt: at,
  };
};

const previewHubSpot = () => /crmTheme=hubspot/.test(window.location.search);
const previewIsAdmin = () => new URLSearchParams(window.location.search).get("role") !== "user";
const PREVIEW_ME = "preview@example.com";

const listJobChanges = (req: { scope?: string; status?: string; from?: number | null; to?: number | null; page?: number; pageSize?: number } | null) => {
  const q = req || {};
  const isAdmin = previewIsAdmin();
  const scope = isAdmin && q.scope !== "mine" ? "team" : "mine";
  const status = ["pending", "applied", "rejected", "superseded", "all"].includes(String(q.status)) ? String(q.status) : "pending";
  const pageSize = Math.min(Math.max(Number(q.pageSize) || 50, 1), 100);
  const page = Math.max(Number(q.page) || 0, 0);
  const inScope = PREVIEW_JOB_CHANGES.map((j) => previewJobChangeRow(j, previewHubSpot())).filter((r) => scope === "team" || r.ownerEmails.includes(PREVIEW_ME));
  const rows = inScope
    .filter((r) => status === "all" || r.status === status)
    .filter((r) => (!q.from || r.detectedAt >= q.from) && (!q.to || r.detectedAt <= q.to))
    .sort((a, b) => b.detectedAt - a.detectedAt);
  return {
    result: {
      rows: rows.slice(page * pageSize, page * pageSize + pageSize),
      scope,
      status,
      page,
      pageSize,
      hasMore: rows.length > (page + 1) * pageSize,
      pendingCount: inScope.filter((r) => r.status === "pending").length,
      isAdmin,
      crm: previewHubSpot() ? "hubspot" : "outreach",
      // ?jobChanges=auto previews an organization that applies job changes automatically.
      settings: { mode: "updateRecord", companyCreation: "create", autoApply: new URLSearchParams(window.location.search).get("jobChanges") === "auto", ownerTask: false },
    },
    status: 200,
  };
};

const resolveJobChanges = (req: { keys: string[]; action: "approve" | "reject" }) => {
  const results = (req.keys || []).map((key) => {
    const j = PREVIEW_JOB_CHANGES.find((x) => x.key === key);
    if (!j) return { key, recordId: null, ok: false, status: null, message: "This job change was not found." };
    if (!previewIsAdmin() && !j.owners.includes(PREVIEW_ME)) return { key, recordId: j.recordId, ok: false, status: j.status, message: "Only the record's owner or an admin can do this." };
    if (j.status !== "pending") return { key, recordId: j.recordId, ok: false, status: j.status, message: `This job change was already ${j.status}.` };
    if (req.action === "approve" && j.lastError) return { key, recordId: j.recordId, ok: false, status: "pending" as const, outcome: "company_missing", message: j.lastError };
    j.status = req.action === "approve" ? "applied" : "rejected";
    return { key, recordId: j.recordId, ok: true, status: j.status, outcome: req.action === "approve" ? "updated" : null, message: null };
  });
  return {
    result: {
      action: req.action,
      results,
      applied: results.filter((r) => r.ok && r.status === "applied").length,
      rejected: results.filter((r) => r.ok && r.status === "rejected").length,
      failed: results.filter((r) => !r.ok).length,
    },
    status: 200,
  };
};

const RESPONSES: Record<string, (req: never) => unknown> = {
  getAddonStatus: addonStatus,
  getDialerSettings: () => ({ result: { settings: dialerSettings(), outreachDispositions: null, organizationId: "dev-crm-preview-org" }, status: 200 }),
  getMyDialerSettings: () => ({ result: { org: dialerSettings(), mine: {}, effective: dialerSettings() }, status: 200 }),
  getLinkedInAutomation: linkedInAutomation,
  getExportHistory: exportHistory,
  listJobChanges,
  resolveJobChanges,
  getBillingStatus: billingStatus,
  listBillingInvoices: billingInvoices,
  getUsageReport: usageReport,
  getPhoneEnrichmentSettings: () => ({
    result: {
      settings: {
        autoEnrich: true,
        numberScope: "mobile_or_direct",
        rules: [
          { id: "r1", kind: "job_title_contains", values: ["VP", "Director", "Head"], enabled: true },
          { id: "r2", kind: "has_open_call_task", enabled: true },
        ],
        manualEnabled: true,
      },
    },
    status: 200,
  }),
  getProfileEnrichmentSettings: () => ({ result: { settings: { standaloneLookup: false } }, status: 200 }),
  getExportSettings: () => ({
    result: {
      settings: {
        overwriteExisting: false,
        findEmail: true,
        findPhone: true,
        outreach: { prospect: { custom1: "headline" } },
        tagRules: [{ id: "o1", match: "any", conditions: [{ entity: "person", field: "title", operator: "contains", values: ["VP", "Head", "Chief"] }], tags: ["Exec"] }],
        sequenceRouting: {
          outreach: {
            enabled: true,
            defaultSequenceId: "10",
            defaultSequenceName: "LinkedIn general",
            rules: [{ id: "s1", match: "all", conditions: [{ entity: "person", field: "country", operator: "is", values: ["Israel"] }], sequenceId: "11", sequenceName: "Israel outbound" }],
          },
        },
      },
      defaults: {
        outreach: { prospect: { firstName: "firstName", lastName: "lastName", title: "title", company: "company", linkedInUrl: "linkedinUrl", source: "{boring}" }, account: { name: "name", domain: "domain" } },
        hubspot: { contact: { firstname: "firstName", lastname: "lastName", jobtitle: "title", state: "state", hs_linkedin_url: "linkedinUrl" }, company: { name: "name", domain: "domain" } },
      },
      organizationId: "dev-crm-preview-org",
      canFindPhone: true,
      // ?role=user previews a rep, who sees the settings read-only.
      canEdit: new URLSearchParams(window.location.search).get("role") !== "user",
    },
    status: 200,
  }),
  updateExportSettings: (req: { settings: unknown }) => ({ result: { ...(RESPONSES.getExportSettings as any)().result, settings: req.settings }, status: 200 }),
  listExportFields: (req: { destination: string; record: string }) => ({
    result: {
      fields:
        req.destination === "outreach"
          ? req.record === "account"
            ? [
                { name: "name", label: "Name", type: "string", group: "Standard" },
                { name: "domain", label: "Domain", type: "string", group: "Standard" },
                { name: "industry", label: "Industry", type: "string", group: "Standard" },
                { name: "custom4", label: "ICP tier", type: "string", group: "Custom" },
              ]
            : [
                { name: "firstName", label: "First name", type: "string", group: "Standard" },
                { name: "lastName", label: "Last name", type: "string", group: "Standard" },
                { name: "title", label: "Title", type: "string", group: "Standard" },
                { name: "addressCity", label: "City", type: "string", group: "Standard" },
                { name: "addressState", label: "State", type: "string", group: "Standard" },
                { name: "linkedInUrl", label: "LinkedIn URL", type: "string", group: "Standard" },
                { name: "source", label: "Source", type: "string", group: "Standard" },
                { name: "custom1", label: "LinkedIn headline", type: "string", group: "Custom" },
                { name: "custom3", label: "Lead source detail", type: "string", group: "Custom" },
              ]
          : req.record === "company"
          ? [
              { name: "name", label: "Company name", type: "text" },
              { name: "domain", label: "Company domain name", type: "text" },
              { name: "industry", label: "Industry", type: "select" },
              { name: "linkedin_company_page", label: "LinkedIn company page", type: "text" },
            ]
          : [
              { name: "firstname", label: "First name", type: "text" },
              { name: "lastname", label: "Last name", type: "text" },
              { name: "jobtitle", label: "Job title", type: "text" },
              { name: "city", label: "City", type: "text" },
              { name: "state", label: "State/Region", type: "text" },
              { name: "country", label: "Country/Region", type: "text" },
              { name: "hs_linkedin_url", label: "LinkedIn URL", type: "text" },
              { name: "lead_source_detail", label: "Lead source detail", type: "text" },
            ],
    },
    status: 200,
  }),
  listExportSequences: () => ({
    result: { crm: "outreach", sequences: [{ id: "10", name: "LinkedIn general" }, { id: "11", name: "Israel outbound" }, { id: "12", name: "My warm intro" }, { id: "13", name: "Enterprise exec" }] },
    status: 200,
  }),
  listExportStages: () => ({
    result: {
      crm: new URLSearchParams(window.location.search).get("crmTheme") === "hubspot" ? "hubspot" : "outreach",
      hubspot: {
        lifecyclestage: [
          { value: "subscriber", label: "Subscriber" },
          { value: "lead", label: "Lead" },
          { value: "marketingqualifiedlead", label: "Marketing Qualified Lead" },
          { value: "salesqualifiedlead", label: "Sales Qualified Lead" },
          { value: "opportunity", label: "Opportunity" },
          { value: "customer", label: "Customer" },
        ],
        hs_lead_status: [
          { value: "NEW", label: "New" },
          { value: "OPEN", label: "Open" },
          { value: "IN_PROGRESS", label: "In Progress" },
        ],
      },
      outreach: [
        { value: "1", label: "Cold" },
        { value: "2", label: "Approaching" },
        { value: "3", label: "Replied" },
        { value: "4", label: "Interested" },
      ],
    },
    status: 200,
  }),
  getProductLicenseRules: () => ({ result: { rules: {} }, status: 200 }),
  getHubSpotLeadStatuses: () => ({
    result: [
      { value: "NEW", label: "New" },
      { value: "OPEN", label: "Open" },
      { value: "IN_PROGRESS", label: "In Progress" },
      { value: "BAD_TIMING", label: "Bad Timing" },
      { value: "UNQUALIFIED", label: "Unqualified" },
    ],
    status: 200,
  }),
  getOutreachProspectStages: () => ({
    result: [
      { id: "1", name: "Cold", order: 1 },
      { id: "2", name: "Approaching", order: 2 },
      { id: "3", name: "Interested", order: 3 },
      { id: "6", name: "Unresponsive", order: 6 },
      { id: "7", name: "Moved on", order: 7 },
    ],
    status: 200,
  }),
  // The preview saves nowhere; the settings page shows "Saved." as it would.
  updateOrgSettings: () => ({ result: { organizationUpdated: true, userUpdated: false }, status: 200 }),
  getChartRangeStats: chartRangeStats,
  getInsightsReport: insightsReport,
  getHubSpotUsers: hubSpotUsers,
  getOutreachUsers: outreachUsers,
};

/** The preview's answer to a backend call, or null to make the real call. */
export const previewResponse = (name: string, request: unknown): Promise<{ data: unknown }> | null => {
  if (!isDevPreviewActive()) return null;
  const make = RESPONSES[name];
  if (!make) return null;
  return Promise.resolve({ data: make(request as never) });
};
